import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

const PLAID = `
  repeating-linear-gradient(90deg, #6b3a2a 0 3px, transparent 3px 11px),
  repeating-linear-gradient(#6b3a2a 0 3px, #e4c79a 3px 11px)
`;

const CAMO = `
  radial-gradient(circle at 18% 32%, #3d4f34 0 16px, transparent 17px),
  radial-gradient(circle at 62% 28%, #8a7348 0 18px, transparent 19px),
  radial-gradient(circle at 40% 72%, #2a3326 0 14px, transparent 15px),
  radial-gradient(circle at 78% 70%, #c4b48a 0 12px, transparent 13px),
  linear-gradient(#5c684e, #5c684e)
`;

const straighten = keyframes`
  0%, 14% { transform: rotate(-16deg) scale(1); opacity: 1; }
  36%, 46% { transform: rotate(0deg) scale(1); opacity: 1; }
  64%, 100% { transform: rotate(0deg) scale(0.42) translate(210px, 8px); opacity: 0; }
`;

const rulerFade = keyframes`
  0%, 42% { opacity: 1; }
  58%, 100% { opacity: 0; }
`;

const gridIn = keyframes`
  0%, 52% { opacity: 0; transform: scale(0.96); }
  68%, 90% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; }
`;

const seamClose = keyframes`
  0%, 18% { transform: scaleX(1); opacity: 0.95; }
  46%, 100% { transform: scaleX(0); opacity: 0; }
`;

const repeatIn = keyframes`
  0%, 48% { opacity: 0; }
  66%, 90% { opacity: 1; }
  100% { opacity: 0; }
`;

const bouquetBob = keyframes`
  0%, 28% { transform: translate(0, 0) scale(1); opacity: 1; }
  48%, 58% { transform: translate(36px, -6px) scale(0.72); opacity: 0.85; }
  78%, 100% { transform: translate(36px, -6px) scale(0.2); opacity: 0; }
`;

const fieldBloom = keyframes`
  0%, 42% { opacity: 0; transform: scale(0.92); }
  62%, 90% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 112,
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

function TilePreview() {
  return (
    <Stage tint="#f3efe8">
      <Box
        sx={{
          position: "absolute",
          left: 28,
          top: 22,
          width: 54,
          height: 42,
          backgroundImage: PLAID,
          transformOrigin: "center",
          animation: `${straighten} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 18,
          top: 74,
          width: 74,
          height: 8,
          borderRadius: 1,
          bgcolor: "#f7f1e4",
          border: "1px solid #c4b49a",
          backgroundImage:
            "repeating-linear-gradient(90deg, #8a7355 0 1px, transparent 1px 7px)",
          animation: `${rulerFade} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 18,
          width: 78,
          height: 76,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2px",
          animation: `${gridIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ backgroundImage: PLAID }} />
        ))}
      </Box>
    </Stage>
  );
}

function ClothPreview() {
  return (
    <Stage tint="#efeae3">
      <Box
        sx={{
          position: "absolute",
          left: 22,
          top: 18,
          width: 72,
          height: 76,
          backgroundImage: CAMO,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 0,
            width: 8,
            height: "100%",
            ml: "-4px",
            bgcolor: "#f6f1e8",
            transformOrigin: "center",
            animation: `${seamClose} 4.8s ease-in-out infinite`,
            ...motionSafe,
          }}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 18,
          width: 78,
          height: 76,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2px",
          animation: `${repeatIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ backgroundImage: CAMO }} />
        ))}
      </Box>
    </Stage>
  );
}

function InventPreview() {
  const bloom = `
    radial-gradient(circle at 22% 28%, #c45a7a 0 7px, transparent 8px),
    radial-gradient(circle at 58% 22%, #6b8f3a 0 6px, transparent 7px),
    radial-gradient(circle at 78% 48%, #d4a017 0 5px, transparent 6px),
    radial-gradient(circle at 36% 68%, #c45a7a 0 6px, transparent 7px),
    radial-gradient(circle at 68% 72%, #6b8f3a 0 7px, transparent 8px),
    linear-gradient(#f4f0e6, #f4f0e6)
  `;
  return (
    <Stage tint="#f7f3eb">
      <Box
        sx={{
          position: "absolute",
          left: 24,
          top: 28,
          width: 44,
          height: 52,
          borderRadius: "50% 50% 46% 46%",
          background:
            "radial-gradient(circle at 50% 38%, #c45a7a 0 10px, #6b8f3a 11px 18px, transparent 19px), linear-gradient(#f7f3eb, #f7f3eb)",
          border: "1px solid #e2d6c4",
          animation: `${bouquetBob} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 14,
          top: 16,
          width: 86,
          height: 80,
          borderRadius: 1,
          backgroundImage: bloom,
          animation: `${fieldBloom} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function ToolCard({ selected, accent, title, body, preview, onSelect }) {
  const theme = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 3,
        overflow: "hidden",
        border: "2px solid",
        borderColor: selected ? accent : "divider",
        bgcolor: "background.paper",
        boxShadow: selected ? `0 10px 28px ${alpha(accent, 0.18)}` : "none",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        font: "inherit",
        color: "inherit",
        "&:hover": {
          borderColor: selected ? accent : alpha(accent, 0.45),
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {preview}
        {selected ? (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: accent,
              bgcolor: "#fff",
              borderRadius: "50%",
              fontSize: 22,
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ p: 1.75 }}>
        <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.6, lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

function pickKey(value) {
  const kind = String(value?.Kind || "")
    .trim()
    .toLowerCase();
  if (kind === "tartan" || kind === "tile") return "tartan";
  if (kind === "seamless" || kind === "interpretacion") return "seamless";
  return "";
}

export default function SkillAgentRapportPickDialog({
  open,
  onClose,
  value,
  onChange,
  error,
  onClearError,
  submitting,
  onSubmit,
}) {
  const { t } = useTranslation();
  const selected = pickKey(value);
  const branch =
    [value?.CustomerName, value?.Temporada].filter(Boolean).join(" · ") ||
    t("skillAgent.common.emDash");

  const selectPath = (path) => {
    if (path === "tartan") {
      onChange?.((prev) => ({ ...prev, Kind: "tartan", Intent: "" }));
      return;
    }
    onChange?.((prev) => ({ ...prev, Kind: "seamless", Intent: "" }));
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("skillAgent.dialog.newRapportTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("skillAgent.dialog.newRapportLead")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.75} sx={{ pt: 1.25 }}>
          {error ? (
            <Alert severity="error" onClose={onClearError}>
              {error}
            </Alert>
          ) : null}
          <Box
            sx={{
              px: 1.5,
              py: 1.1,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.warning.main, 0.08),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              {t("skillAgent.dialog.treeBranchLabel")}
            </Typography>
            <Typography variant="body1" fontWeight={800}>
              {branch}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <ToolCard
              selected={selected === "tartan"}
              accent="#a16207"
              title={t("skillAgent.dialog.rapportTartanTitle", {
                defaultValue: "Tartán",
              })}
              body={t("skillAgent.dialog.rapportTartanBody", {
                defaultValue:
                  "Foto de cuadro, tartán o raya. Vectorton lee hilos y colores y saca el módulo SVG para el mill.",
              })}
              preview={<TilePreview />}
              onSelect={() => selectPath("tartan")}
            />
            <ToolCard
              selected={selected === "seamless"}
              accent="#6d28d9"
              title={t("skillAgent.dialog.rapportSeamlessTitle", {
                defaultValue: "Seamless",
              })}
              body={t("skillAgent.dialog.rapportSeamlessBody", {
                defaultValue:
                  "Print, leopard, floral o camo. Vectorton hace tileable el mismo motivo (fidelidad a la foto).",
              })}
              preview={<ClothPreview />}
              onSelect={() => selectPath("seamless")}
            />
          </Stack>
          <TextField
            size="small"
            label={t("skillAgent.form.title")}
            placeholder={t("skillAgent.dialog.newRapportTitlePlaceholder")}
            value={value?.Titulo || ""}
            onChange={(e) =>
              onChange?.((prev) => ({ ...prev, Titulo: e.target.value }))
            }
            required
            fullWidth
            sx={pageFieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          sx={pagePrimaryButtonSx}
          disabled={submitting || !selected}
          onClick={onSubmit}
        >
          {submitting
            ? t("skillAgent.common.creating")
            : t("skillAgent.dialog.newRapportSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Diálogo corto cuando un interpretacion viejo no tiene intent guardado. */
export function SkillAgentRapportIntentDialog({ open, submitting, onPick }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("skillAgent.dialog.intentTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("skillAgent.dialog.intentLead")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ pt: 1 }}
        >
          <ToolCard
            selected={false}
            accent="#6d28d9"
            title={t("skillAgent.dialog.rapportClothTitle")}
            body={t("skillAgent.dialog.rapportClothBody")}
            preview={<ClothPreview />}
            onSelect={() => !submitting && onPick?.("cloth")}
          />
          <ToolCard
            selected={false}
            accent="#0f766e"
            title={t("skillAgent.dialog.rapportInventTitle")}
            body={t("skillAgent.dialog.rapportInventBody")}
            preview={<InventPreview />}
            onSelect={() => !submitting && onPick?.("invent")}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
